// CardToCardPayment.jsx
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import BASE_URL from "../../config/config";

const isValidCardNumberLuhn = (cardNumber) => {
  let sum = 0;
  let alternate = false;
  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cardNumber[i], 10);
    if (alternate) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    alternate = !alternate;
  }
  return sum % 10 === 0;
};

const CardToCardPayment = ({
  courseData,
  discount,
  csrfToken,
  jalaliTodayDateOnly,
  onBack,
}) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: "",
    trackingNumber: "",
    paymentDate: "",
  });
  const [paymentSlipFile, setPaymentSlipFile] = useState(null);
  const [slipPreview, setSlipPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const validateCardNumber = (num) => {
    if (!/^\d{16}$/.test(num)) return false;
    return isValidCardNumberLuhn(num);
  };
  const validateTrackingNumber = (num) => /^\d{4,12}$/.test(num);
  const validatePaymentDate = (date) => /^\d{4}\/\d{2}\/\d{2}$/.test(date);
  const validatePaymentSlip = (file) => {
    if (!file) return false;
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "application/pdf",
    ];
    return allowedTypes.includes(file.type);
  };

  const calculateFinalPrice = (price, discountPercent) => {
    const originalPrice = parseFloat(price) || 0;
    const discount = parseFloat(discountPercent) || 0;
    return originalPrice - (originalPrice * discount) / 100;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "cardNumber" && (/[^0-9]/.test(value) || value.length > 16))
      return;
    if (
      name === "trackingNumber" &&
      (/[^0-9]/.test(value) || value.length > 12)
    )
      return;
    if (name === "paymentDate" && (/[^0-9/]/.test(value) || value.length > 10))
      return;
    setPaymentInfo({ ...paymentInfo, [name]: value });

    let errorMsg = "";
    if (name === "cardNumber")
      errorMsg = validateCardNumber(value)
        ? ""
        : "شماره کارت باید یک شماره معتبر 16 رقمی باشد";
    else if (name === "trackingNumber")
      errorMsg = validateTrackingNumber(value)
        ? ""
        : "شماره پیگیری باید ۴ تا ۱۲ رقم عدد باشد";
    else if (name === "paymentDate")
      errorMsg =
        validatePaymentDate(value) || value === ""
          ? ""
          : "فرمت تاریخ باید YYYY/MM/DD باشد";
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && !validatePaymentSlip(file)) {
      setErrors((prev) => ({
        ...prev,
        paymentSlip: "فایل باید تصویر JPG، PNG، GIF یا PDF باشد",
      }));
      setPaymentSlipFile(null);
    } else {
      setErrors((prev) => ({ ...prev, paymentSlip: "" }));
      setPaymentSlipFile(file);
    }
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setSlipPreview(objectUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const finalErrors = {
      cardNumber: validateCardNumber(paymentInfo.cardNumber)
        ? ""
        : "شماره کارت باید ۱۶ رقم عدد باشد",
      trackingNumber: validateTrackingNumber(paymentInfo.trackingNumber)
        ? ""
        : "شماره پیگیری باید ۴ تا ۱۲ رقم عدد باشد",
      paymentDate: validatePaymentDate(paymentInfo.paymentDate)
        ? ""
        : "فرمت تاریخ باید YYYY/MM/DD باشد",
      paymentSlip:
        paymentSlipFile && validatePaymentSlip(paymentSlipFile)
          ? ""
          : "فایل باید تصویر JPG، PNG، GIF یا PDF باشد",
    };
    setErrors(finalErrors);
    if (Object.values(finalErrors).some((err) => err !== "")) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("service_type", "register");
      formData.append("course_id", courseData ? courseData.id : 0);
      formData.append("description", JSON.stringify({ ...paymentInfo }));
      formData.append(
        "price",
        calculateFinalPrice(courseData.price, discount)
      );
      formData.append("paymentDate", paymentInfo.paymentDate);
      formData.append("paymentSlip", paymentSlipFile);
      formData.append("payment_method", "card_to_card");

      const response = await fetch(`${BASE_URL}/orders`, {
        method: "POST",
        headers: {
          "CSRF-Token": csrfToken,
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        credentials: "include",
        body: formData,
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.message || "خطا در ثبت سفارش");

      alert("سفارش شما ثبت شده و در حال بررسی است.");
      setTimeout(() => navigate("/dashboard/courses"), 3000);
    } catch (error) {
      alert("خطایی رخ داده است: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="card-to-card-payment">
      <button className="back-button" onClick={onBack}>
        ← بازگشت به انتخاب روش پرداخت
      </button>

      <div className="destination-section">
        <h3>شماره کارت‌های مقصد</h3>
        <ul className="card-list">
          <li className="card-item">
            <span className="card-number">6037-9971-8046-4597</span>
            <span className="card-name">بانک ملی</span>
          </li>
          <li className="card-item">
            <span className="card-number">5859-8318-2987-4956</span>
            <span className="card-name">بانک تجارت</span>
          </li>
          <li className="card-item">
            <span className="card-number">6037-6915-4580-2456</span>
            <span className="card-name">بانک صادرات</span>
          </li>
        </ul>
        <h3>بنام مصعب شریفی</h3>
      </div>

      <form onSubmit={handleSubmit} encType="multipart/form-data">
        <div className="form-group">
          <label htmlFor="cardNumber">
            شماره کارت (16 رقم) پرداخت کننده
          </label>
          <input
            type="text"
            id="cardNumber"
            name="cardNumber"
            value={paymentInfo.cardNumber}
            onChange={handleChange}
            maxLength={16}
            className="form-control"
            placeholder="xxxx xxxx xxxx xxxx"
            required
          />
          {errors.cardNumber && (
            <div className="error">{errors.cardNumber}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="trackingNumber">شماره پیگیری (4 تا 12 رقم)</label>
          <input
            type="text"
            id="trackingNumber"
            name="trackingNumber"
            value={paymentInfo.trackingNumber}
            onChange={handleChange}
            maxLength={12}
            className="form-control"
            placeholder="****"
            required
          />
          {errors.trackingNumber && (
            <div className="error">{errors.trackingNumber}</div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="paymentDate">تاریخ پرداخت</label>
          <input
            type="text"
            id="paymentDate"
            name="paymentDate"
            value={paymentInfo.paymentDate}
            onChange={handleChange}
            maxLength={10}
            className="form-control"
            placeholder={jalaliTodayDateOnly}
            required
          />
          {errors.paymentDate && (
            <div className="error">{errors.paymentDate}</div>
          )}
        </div>

        <div className="file-input-wrapper">
          <input
            type="file"
            id="paymentSlip"
            accept=".jpg,.jpeg,.png,.gif,.pdf"
            onChange={handleFileChange}
            required
          />
          <label htmlFor="paymentSlip" className="file-input-label">
            <span className="upload-icon">📎</span>
            آپلود عکس رسید واریزی
          </label>
          {slipPreview && (
            <div className="image-preview">
              <img src={slipPreview} alt="پیش‌نمایش فیش" />
              <button
                type="button"
                className="remove-image"
                onClick={() => {
                  setPaymentSlipFile(null);
                  setSlipPreview(null);
                }}
              >
                ✕ حذف تصویر
              </button>
            </div>
          )}
        </div>
        {errors.paymentSlip && (
          <div
            className="error"
            style={{ textAlign: "center", marginTop: "-15px" }}
          >
            {errors.paymentSlip}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="submit-button btn-pay"
        >
          {isLoading ? "در حال ثبت..." : "📤 تأیید و ارسال"}
        </button>
      </form>
    </div>
  );
};

export default CardToCardPayment;
